import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Upload, X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { api, MATERIALS } from "@/lib/api";

const inputCls = "w-full bg-white border border-navy/20 px-4 py-3 text-navy placeholder:text-navy/40 focus:outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors";

const SubmitOffer = () => {
  const empty = { company_name: "", contact_name: "", email: "", phone: "", material_type: "", quantity: "", price: "", location: "", message: "" };
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = (e) => {
    const selected = Array.from(e.target.files);
    const tooBig = selected.find((f) => f.size > 2.5 * 1024 * 1024);
    if (tooBig) { toast.error("Each photo must be under 2.5MB."); return; }
    const chosen = selected.slice(0, 3);
    Promise.all(chosen.map((f) => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => res({ filename: f.name, content_type: f.type || "image/jpeg", data: String(reader.result).split(",")[1] });
      reader.readAsDataURL(f);
    }))).then(setFiles);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.material_type || !form.quantity) {
      toast.error("Please complete all required fields."); return;
    }
    setLoading(true);
    try {
      await api.post("/offers", { ...form, attachments: files });
      toast.success("Offer submitted! Our team will review and respond shortly.");
      setForm(empty);
      setFiles([]);
      setDone(true);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="submit-offer-page">
      <PageHero eyebrow="/ SELL YOUR MATERIAL" title={["Sell Your Non-Prime", "or Off-Grade Material."]}
        subtitle="Direct-purchase program for producers with recurring post-industrial resin, paper or PPE. We buy FTL, FCL, mixed and combination loads on recurring contracts across North America." />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ HOW IT WORKS</div>
          <div className="space-y-8 mt-8">
            {[["01", "Submit your material details"], ["02", "We review & send you a purchase offer"], ["03", "Pickup scheduled — FTL / FCL / mixed"], ["04", "Fast payment on collection"]].map(([n, t]) => (
              <div key={n} className="flex gap-4">
                <span className="font-mono text-emerald">{n}</span>
                <span className="text-navy text-lg font-display font-bold tracking-tight">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7 md:col-start-6">
          {done ? (
            <FadeUp>
              <div className="border border-emerald bg-emerald/5 p-10 text-center" data-testid="offer-success">
                <CheckCircle2 className="text-emerald mx-auto mb-6" size={48} />
                <h3 className="font-display font-black text-2xl text-navy tracking-tight">Offer received</h3>
                <p className="text-navy/60 mt-3">Thank you — our team will get back to you soon.</p>
                <button onClick={() => setDone(false)} data-testid="offer-another"
                  className="mt-8 inline-flex bg-navy text-white font-mono text-xs uppercase tracking-widest px-6 py-3 items-center gap-2 hover:bg-emerald hover:text-navy transition-colors">
                  Submit another <ArrowRight size={14} />
                </button>
              </div>
            </FadeUp>
          ) : (
            <FadeUp>
              <form onSubmit={submit} className="space-y-5" data-testid="offer-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input className={inputCls} placeholder="Company name *" value={form.company_name} onChange={set("company_name")} data-testid="offer-company" />
                  <input className={inputCls} placeholder="Contact name *" value={form.contact_name} onChange={set("contact_name")} data-testid="offer-contact" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input className={inputCls} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="offer-email" />
                  <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="offer-phone" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <select className={inputCls} value={form.material_type} onChange={set("material_type")} data-testid="offer-material">
                    <option value="">Material type *</option>
                    {MATERIALS.map((m) => <option key={m.name} value={m.name}>{m.code} — {m.name}</option>)}
                    <option value="Mixed / Combination Load">Mixed / Combination Load</option>
                    <option value="Other">Other</option>
                  </select>
                  <input className={inputCls} placeholder="Quantity (e.g. 20,000 lbs / 1 FTL) *" value={form.quantity} onChange={set("quantity")} data-testid="offer-quantity" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input className={inputCls} placeholder="Asking price (optional)" value={form.price} onChange={set("price")} data-testid="offer-price" />
                  <input className={inputCls} placeholder="Location / city" value={form.location} onChange={set("location")} data-testid="offer-location" />
                </div>
                <textarea className={`${inputCls} min-h-[130px] resize-none`} placeholder="Additional details (material condition, packaging, frequency...)" value={form.message} onChange={set("message")} data-testid="offer-notes" />

                <div>
                  <label className="flex items-center gap-3 border border-dashed border-navy/30 px-4 py-4 cursor-pointer hover:border-emerald transition-colors" data-testid="offer-photos-label">
                    <Upload size={20} className="text-emerald" />
                    <span className="text-navy/70 text-sm">Attach photos of your material (optional, up to 3, max 2.5MB each)</span>
                    <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="offer-photos-input" />
                  </label>
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-3" data-testid="offer-photos-preview">
                      {files.map((f, i) => (
                        <div key={i} className="relative w-20 h-20 border border-navy/10">
                          <img src={`data:${f.content_type};base64,${f.data}`} alt={f.filename} className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                            className="absolute -top-2 -right-2 bg-navy text-white w-5 h-5 flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading} data-testid="offer-submit"
                  className="w-full bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
                  {loading ? "Submitting..." : "Submit Offer"}
                </button>
              </form>
            </FadeUp>
          )}
        </div>
      </section>
    </div>
  );
};

export default SubmitOffer;
